import React, { useLayoutEffect, useRef, useState } from 'react';
import Tooltip from '@material-ui/core/Tooltip';
import useElementSizeUpdate from 'Hooks/useElementSizeUpdate';
import { Wrapper, MeasureRow, Category, CategoryLabel, MoreCategory } from './styled';

const GAP = 8; // must match the `gap` set on Wrapper/MeasureRow

const Categories = ({ data }) => {
  const wrapperRef = useRef(null);
  const measureRefs = useRef([]);
  const moreRef = useRef(null);
  const { width } = useElementSizeUpdate(wrapperRef);
  const [visibleCount, setVisibleCount] = useState(data?.length || 0);

  useLayoutEffect(() => {
    if (!data?.length || !wrapperRef.current || !moreRef.current) return;

    const available = wrapperRef.current.clientWidth;
    const chips = measureRefs.current.slice(0, data.length);
    if (chips.some((el) => !el)) return;

    const totalWidth = chips.reduce((sum, el, i) => sum + el.offsetWidth + (i > 0 ? GAP : 0), 0);
    if (totalWidth <= available) {
      setVisibleCount(data.length);
      return;
    }

    // Real measured width of the "+N" chip (worst case: as many digits as there are
    // categories), not a guessed constant — a guessed reserve that runs bigger than the
    // real chip hides categories that would actually have fit.
    const moreWidth = moreRef.current.offsetWidth;

    let used = 0;
    let count = 0;
    for (let i = 0; i < chips.length; i += 1) {
      const chipWidth = chips[i].offsetWidth + (i > 0 ? GAP : 0);
      if (used + chipWidth + GAP + moreWidth > available) break;
      used += chipWidth;
      count += 1;
    }
    // count can legitimately be 0 (not even one chip fits alongside a "+N" badge) — in
    // that case show only the "+N" badge for every category instead of forcing a chip
    // that would render clipped with no overflow indicator.
    setVisibleCount(count);
  }, [data, width]);

  if (!data?.length) return null;

  const hidden = data.slice(visibleCount);

  return (
    <Wrapper ref={ wrapperRef }>
      { /* Off-screen, always-full-width copy used only to measure real chip widths — the
         visible chips below get trimmed from this, so a resize never measures an
         already-hidden (0-width) chip. Includes a "+N" probe so its reserve is measured,
         not guessed. */ }
      <MeasureRow aria-hidden="true">
        { data.map((tag, i) => (
          <Category key={ tag } ref={ (el) => { measureRefs.current[i] = el; } }><CategoryLabel>{ tag }</CategoryLabel></Category>
        )) }
        <MoreCategory ref={ moreRef }><CategoryLabel>{ `+${data.length}` }</CategoryLabel></MoreCategory>
      </MeasureRow>
      { data.slice(0, visibleCount).map((tag) => (
        <Category key={ tag } title={ tag }><CategoryLabel>{ tag }</CategoryLabel></Category>
      )) }
      { hidden.length > 0 &&
        <Tooltip title={ hidden.join(', ') }>
          <MoreCategory tabIndex={ 0 }><CategoryLabel>{ `+${hidden.length}` }</CategoryLabel></MoreCategory>
        </Tooltip>
      }
    </Wrapper>
  );
};

export default Categories;
